import { useEffect, useState } from "react";

const PHASE1_DURATION = 2000;
const PHASE2_DURATION = 9000;
const ANIMATION_TOTAL = 14000;

const LogoAnimation = ({ onComplete }) => {
  const [phase, setPhase] = useState(1);
  const [isAnimating, setIsAnimating] = useState(true);

  const finish = () => {
    if (!isAnimating) return;
    setIsAnimating(false);
    if (onComplete) onComplete();
  };

  useEffect(() => {
    const phase1Timer = setTimeout(() => setPhase(2), PHASE1_DURATION);
    const phase2Timer = setTimeout(() => setPhase(3), PHASE2_DURATION);
    const completeTimer = setTimeout(() => {
      setIsAnimating(false);
      if (onComplete) onComplete();
    }, ANIMATION_TOTAL);

    return () => {
      clearTimeout(phase1Timer);
      clearTimeout(phase2Timer);
      clearTimeout(completeTimer);
    };
  }, [onComplete]);

  if (!isAnimating) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black overflow-hidden" onClick={finish} data-testid="logo-animation">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          finish();
        }}
        data-testid="skip-intro-btn"
        className="absolute top-4 right-4 z-[10000] text-white/70 hover:text-white text-xs md:text-sm font-medium bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 px-4 py-2 rounded-full transition-all"
      >
        Skip Intro
      </button>
      {/* Lightning Bolts Background */}
      <div className="lightning-container">
        <div className="lightning lightning-1"></div>
        <div className="lightning lightning-2"></div>
        <div className="lightning lightning-3"></div>
        <div className="lightning lightning-4"></div>
        <div className="lightning lightning-5"></div>
        <div className="lightning lightning-6"></div>
      </div>

      {/* Animated Background Particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(30)].map((_, i) => (
          <div
            key={`particle-${i}`}
            className="ice-particle-bg"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${3 + Math.random() * 4}s`
            }}
          />
        ))}
      </div>

      {/* Electric Arcs */}
      <div className="electric-arcs">
        <svg className="arc arc-1" viewBox="0 0 100 100" preserveAspectRatio="none">
          <path d="M0,50 Q25,30 50,50 T100,50" stroke="url(#lightning-gradient)" strokeWidth="2" fill="none"/>
        </svg>
        <svg className="arc arc-2" viewBox="0 0 100 100" preserveAspectRatio="none">
          <path d="M0,50 Q25,70 50,50 T100,50" stroke="url(#lightning-gradient)" strokeWidth="2" fill="none"/>
        </svg>
        <defs>
          <linearGradient id="lightning-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="transparent"/>
            <stop offset="50%" stopColor="#67E8F9"/>
            <stop offset="100%" stopColor="transparent"/>
          </linearGradient>
        </defs>
      </div>

      {/* Phase 1 & 2: Hero Image with Text */}
      <div 
        className={`absolute inset-0 flex flex-col items-center justify-center transition-opacity duration-1000 ${
          phase >= 2 && phase < 3 ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {/* Glow Effect Behind Image */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="glow-effect"></div>
        </div>

        {/* Cold Bolt Streams - Left Side */}
        <div className="cold-bolts-container z-20">
          <div className="cold-bolt cold-bolt-left-1"></div>
          <div className="cold-bolt cold-bolt-left-2"></div>
          <div className="cold-bolt cold-bolt-left-3"></div>
          <div className="cold-bolt cold-bolt-right-1"></div>
          <div className="cold-bolt cold-bolt-right-2"></div>
          <div className="cold-bolt cold-bolt-right-3"></div>
          <div className="cold-bolt cold-bolt-top-1"></div>
          <div className="cold-bolt cold-bolt-top-2"></div>
          <div className="cold-bolt cold-bolt-bottom-1"></div>
          <div className="cold-bolt cold-bolt-bottom-2"></div>
        </div>

        {/* Frost Mist Bursts */}
        <div className="frost-burst frost-burst-left z-20"></div>
        <div className="frost-burst frost-burst-right z-20"></div>
        <div className="frost-burst frost-burst-top z-20"></div>

        {/* Cold Air Vapor Trails */}
        <div className="vapor-trail vapor-trail-1 z-20"></div>
        <div className="vapor-trail vapor-trail-2 z-20"></div>
        <div className="vapor-trail vapor-trail-3 z-20"></div>
        <div className="vapor-trail vapor-trail-4 z-20"></div>
        <div className="vapor-trail vapor-trail-5 z-20"></div>
        <div className="vapor-trail vapor-trail-6 z-20"></div>

        {/* Lightning Strike Effects Around Image */}
        <div className="lightning-strikes">
          <div className="strike strike-left"></div>
          <div className="strike strike-right"></div>
          <div className="strike strike-top"></div>
        </div>

        {/* Hero Image */}
        <div className="relative hero-image-container z-10 flex items-center justify-center px-4 md:px-12">
          <div className="relative max-w-2xl md:max-w-3xl w-full">
            <img 
              src="/images/ice-man-cartoon.jpg"
              alt="Ice Man Freezes Pain and Fat"
              className="w-full h-auto object-contain rounded-2xl shadow-2xl"
            />
            {/* Cold Frost Overlay */}
            <div className="frost-overlay"></div>
            {/* Electric Border */}
            <div className="electric-border"></div>
          </div>
        </div>
        
        {/* Tagline Text */}
        <div className="mt-8 text-center px-4 tagline-container z-10">
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white leading-tight text-glow">
            <span className="text-cyan-400">Ice Man</span> Freezes the{" "}
            <span className="text-red-500 pulse-text">Pain</span> and{" "}
            <span className="text-amber-400 pulse-text">Fat</span>
          </h1>
          <p className="text-xl md:text-2xl lg:text-3xl text-white mt-3 font-medium tagline-sub">
            <span className="text-cyan-400 font-bold">Begin Recovery</span>, <span className="text-amber-400 font-bold">Look Amazing</span>
          </p>
        </div>

        {/* Floating Ice Crystals */}
        <div className="ice-crystal ice-crystal-1"></div>
        <div className="ice-crystal ice-crystal-2"></div>
        <div className="ice-crystal ice-crystal-3"></div>
        <div className="ice-crystal ice-crystal-4"></div>
        <div className="ice-crystal ice-crystal-5"></div>
        <div className="ice-crystal ice-crystal-6"></div>
      </div>

      {/* Phase 3: Bouncing Logo with Lightning */}
      <div 
        className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 ${
          phase === 3 ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {/* Logo Lightning Background */}
        <div className="logo-lightning-bg">
          <div className="logo-bolt logo-bolt-1"></div>
          <div className="logo-bolt logo-bolt-2"></div>
          <div className="logo-bolt logo-bolt-3"></div>
          <div className="logo-bolt logo-bolt-4"></div>
        </div>
        
        {/* Energy Ring */}
        <div className="energy-ring energy-ring-1"></div>
        <div className="energy-ring energy-ring-2"></div>
        <div className="energy-ring energy-ring-3"></div>
        
        <div className="bouncing-logo-container">
          <div className="logo-glow-effect"></div>
          <img 
            src="/images/imc-shield.png"
            alt="Ice Man Cryo Logo"
            className="w-72 h-72 md:w-96 md:h-96 lg:w-[500px] lg:h-[500px] object-contain relative z-10"
          />
        </div>
      </div>

      <style jsx>{`
        /* Lightning Container */
        .lightning-container {
          position: absolute;
          inset: 0;
          overflow: hidden;
          pointer-events: none;
        }

        .lightning {
          position: absolute;
          width: 3px;
          background: linear-gradient(to bottom, 
            transparent,
            #67E8F9,
            #fff,
            #67E8F9,
            transparent
          );
          opacity: 0;
          filter: blur(1px);
          box-shadow: 0 0 10px #67E8F9, 0 0 20px #67E8F9, 0 0 30px #67E8F9;
        }

        .lightning-1 {
          left: 10%;
          height: 100%;
          animation: lightningFlash 4s ease-in-out infinite;
          animation-delay: 0s;
        }
        .lightning-2 {
          left: 25%;
          height: 80%;
          top: 10%;
          animation: lightningFlash 4s ease-in-out infinite;
          animation-delay: 0.5s;
        }
        .lightning-3 {
          right: 10%;
          height: 100%;
          animation: lightningFlash 4s ease-in-out infinite;
          animation-delay: 1s;
        }
        .lightning-4 {
          right: 25%;
          height: 70%;
          top: 15%;
          animation: lightningFlash 4s ease-in-out infinite;
          animation-delay: 1.5s;
        }
        .lightning-5 {
          left: 40%;
          height: 60%;
          top: 20%;
          animation: lightningFlash 4s ease-in-out infinite;
          animation-delay: 2s;
        }
        .lightning-6 {
          right: 40%;
          height: 65%;
          top: 18%;
          animation: lightningFlash 4s ease-in-out infinite;
          animation-delay: 2.5s;
        }

        @keyframes lightningFlash {
          0%, 100% { opacity: 0; transform: scaleY(0); }
          5% { opacity: 1; transform: scaleY(1); }
          10% { opacity: 0.3; }
          15% { opacity: 1; }
          20% { opacity: 0; transform: scaleY(0); }
        }

        /* Lightning Strikes around image */
        .lightning-strikes {
          position: absolute;
          inset: -50px;
          pointer-events: none;
        }

        .strike {
          position: absolute;
          background: linear-gradient(90deg, transparent, #67E8F9, #fff, #67E8F9, transparent);
          opacity: 0;
          filter: blur(2px);
        }

        .strike-left {
          left: 0;
          top: 30%;
          width: 100px;
          height: 4px;
          animation: strikeFlash 3s ease-in-out infinite;
        }

        .strike-right {
          right: 0;
          top: 60%;
          width: 100px;
          height: 4px;
          animation: strikeFlash 3s ease-in-out infinite 1s;
        }

        .strike-top {
          top: 0;
          left: 40%;
          width: 4px;
          height: 80px;
          background: linear-gradient(180deg, transparent, #67E8F9, #fff, #67E8F9, transparent);
          animation: strikeFlash 3s ease-in-out infinite 0.5s;
        }

        @keyframes strikeFlash {
          0%, 100% { opacity: 0; }
          10% { opacity: 1; }
          20% { opacity: 0; }
          30% { opacity: 0.8; }
          40% { opacity: 0; }
        }

        /* Background Particles */
        .ice-particle-bg {
          position: absolute;
          width: 4px;
          height: 4px;
          background: rgba(103, 232, 249, 0.8);
          border-radius: 50%;
          animation: floatUp linear infinite;
          box-shadow: 0 0 10px rgba(103, 232, 249, 0.8), 0 0 20px rgba(103, 232, 249, 0.5);
        }

        @keyframes floatUp {
          0% {
            transform: translateY(100vh) scale(0);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateY(-100vh) scale(1);
            opacity: 0;
          }
        }

        /* Glow Effect */
        .glow-effect {
          width: 700px;
          height: 700px;
          background: radial-gradient(circle, rgba(103, 232, 249, 0.3) 0%, rgba(124, 58, 237, 0.4) 40%, transparent 70%);
          filter: blur(60px);
          animation: glowPulse 3s ease-in-out infinite;
        }

        @keyframes glowPulse {
          0%, 100% {
            transform: scale(1);
            opacity: 0.8;
          }
          50% {
            transform: scale(1.3);
            opacity: 1;
          }
        }

        /* Hero Image Animation */
        .hero-image-container {
          animation: heroEntrance 1s ease-out forwards,
                     heroFloat 5s ease-in-out 1s infinite;
        }

        /* Ice Man Blue Tint */
        .ice-man-image {
          filter: hue-rotate(-10deg) saturate(1.1);
        }

        /* Blue Ice Overlay */
        .ice-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: radial-gradient(
            ellipse 30% 25% at 50% 28%,
            rgba(103, 232, 249, 0.25) 0%,
            transparent 70%
          );
          border-radius: 1rem;
          pointer-events: none;
          mix-blend-mode: overlay;
        }

        /* Electric Border */
        .electric-border {
          position: absolute;
          inset: -2px;
          border-radius: 1.2rem;
          background: transparent;
          border: 2px solid transparent;
          animation: electricPulse 2s ease-in-out infinite;
        }

        @keyframes electricPulse {
          0%, 100% {
            box-shadow: 0 0 5px #67E8F9, 0 0 10px #67E8F9, inset 0 0 5px rgba(103, 232, 249, 0.3);
          }
          50% {
            box-shadow: 0 0 20px #67E8F9, 0 0 40px #67E8F9, 0 0 60px #7c3aed, inset 0 0 10px rgba(103, 232, 249, 0.5);
          }
        }

        @keyframes heroEntrance {
          0% {
            opacity: 0;
            transform: scale(1) translateY(20px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        @keyframes heroFloat {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-6px);
          }
        }

        /* Shimmer Effect */
        .shimmer-overlay {
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.3),
            transparent
          );
          animation: shimmer 2.5s ease-in-out infinite;
          border-radius: 1rem;
        }

        @keyframes shimmer {
          0% {
            left: -100%;
          }
          50%, 100% {
            left: 100%;
          }
        }

        /* Frost Border */
        .frost-border {
          position: absolute;
          inset: -4px;
          border-radius: 1.2rem;
          background: linear-gradient(45deg, #67E8F9, #7c3aed, #DAA520, #67E8F9);
          background-size: 300% 300%;
          animation: borderGlow 4s ease infinite;
          z-index: -1;
          opacity: 0.8;
        }

        @keyframes borderGlow {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }

        /* Cold Bolt Streams */
        .cold-bolts-container {
          position: absolute;
          inset: 0;
          pointer-events: none;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .cold-bolt {
          position: absolute;
          background: linear-gradient(90deg, rgba(103, 232, 249, 0.9), rgba(255, 255, 255, 0.95), rgba(103, 232, 249, 0.6), transparent);
          height: 3px;
          opacity: 0;
          filter: blur(1px);
          box-shadow: 0 0 15px rgba(103, 232, 249, 0.8), 0 0 30px rgba(103, 232, 249, 0.5), 0 0 45px rgba(103, 232, 249, 0.3);
          border-radius: 2px;
        }

        .cold-bolt-left-1 {
          top: 35%;
          left: 50%;
          width: 0;
          animation: boltShootLeft 0.8s ease-out 1.2s forwards, boltFade 3s ease-in-out 2s infinite;
        }
        .cold-bolt-left-2 {
          top: 45%;
          left: 50%;
          width: 0;
          height: 4px;
          animation: boltShootLeft 0.6s ease-out 1.4s forwards, boltFade 3.5s ease-in-out 2s infinite;
        }
        .cold-bolt-left-3 {
          top: 55%;
          left: 50%;
          width: 0;
          animation: boltShootLeft 0.7s ease-out 1.6s forwards, boltFade 4s ease-in-out 2.3s infinite;
        }
        .cold-bolt-right-1 {
          top: 35%;
          right: 50%;
          width: 0;
          background: linear-gradient(270deg, rgba(103, 232, 249, 0.9), rgba(255, 255, 255, 0.95), rgba(103, 232, 249, 0.6), transparent);
          animation: boltShootRight 0.8s ease-out 1.2s forwards, boltFade 3s ease-in-out 2s infinite;
        }
        .cold-bolt-right-2 {
          top: 45%;
          right: 50%;
          width: 0;
          height: 4px;
          background: linear-gradient(270deg, rgba(103, 232, 249, 0.9), rgba(255, 255, 255, 0.95), rgba(103, 232, 249, 0.6), transparent);
          animation: boltShootRight 0.6s ease-out 1.4s forwards, boltFade 3.5s ease-in-out 2s infinite;
        }
        .cold-bolt-right-3 {
          top: 55%;
          right: 50%;
          width: 0;
          background: linear-gradient(270deg, rgba(103, 232, 249, 0.9), rgba(255, 255, 255, 0.95), rgba(103, 232, 249, 0.6), transparent);
          animation: boltShootRight 0.7s ease-out 1.6s forwards, boltFade 4s ease-in-out 2.3s infinite;
        }
        .cold-bolt-top-1 {
          top: 50%;
          left: 40%;
          width: 0;
          height: 3px;
          transform: rotate(-45deg);
          transform-origin: right center;
          animation: boltShootLeft 0.5s ease-out 1.3s forwards, boltFade 3s ease-in-out 1.8s infinite;
        }
        .cold-bolt-top-2 {
          top: 50%;
          right: 40%;
          width: 0;
          height: 3px;
          transform: rotate(45deg);
          transform-origin: left center;
          background: linear-gradient(270deg, rgba(103, 232, 249, 0.9), rgba(255, 255, 255, 0.95), rgba(103, 232, 249, 0.6), transparent);
          animation: boltShootRight 0.5s ease-out 1.3s forwards, boltFade 3s ease-in-out 1.8s infinite;
        }
        .cold-bolt-bottom-1 {
          bottom: 40%;
          left: 45%;
          width: 0;
          height: 3px;
          transform: rotate(30deg);
          transform-origin: right center;
          animation: boltShootLeft 0.6s ease-out 1.5s forwards, boltFade 3.5s ease-in-out 2.1s infinite;
        }
        .cold-bolt-bottom-2 {
          bottom: 40%;
          right: 45%;
          width: 0;
          height: 3px;
          transform: rotate(-30deg);
          transform-origin: left center;
          background: linear-gradient(270deg, rgba(103, 232, 249, 0.9), rgba(255, 255, 255, 0.95), rgba(103, 232, 249, 0.6), transparent);
          animation: boltShootRight 0.6s ease-out 1.5s forwards, boltFade 3.5s ease-in-out 2.1s infinite;
        }

        @keyframes boltShootLeft {
          0% { width: 0; opacity: 0; left: 50%; }
          30% { opacity: 1; }
          100% { width: 50vw; opacity: 0.8; left: 0; }
        }
        @keyframes boltShootRight {
          0% { width: 0; opacity: 0; right: 50%; }
          30% { opacity: 1; }
          100% { width: 50vw; opacity: 0.8; right: 0; }
        }
        @keyframes boltFade {
          0%, 100% { opacity: 0.3; box-shadow: 0 0 10px rgba(103, 232, 249, 0.4); }
          50% { opacity: 0.8; box-shadow: 0 0 25px rgba(103, 232, 249, 0.8), 0 0 50px rgba(103, 232, 249, 0.5); }
        }

        /* Frost Mist Bursts */
        .frost-burst {
          position: absolute;
          width: 200px;
          height: 200px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(103, 232, 249, 0.5) 0%, rgba(255, 255, 255, 0.3) 30%, transparent 70%);
          filter: blur(20px);
          opacity: 0;
          pointer-events: none;
        }
        .frost-burst-left {
          top: 40%;
          left: 5%;
          animation: burstIn 1s ease-out 1.5s forwards, burstPulse 4s ease-in-out 2.5s infinite;
        }
        .frost-burst-right {
          top: 40%;
          right: 5%;
          animation: burstIn 1s ease-out 1.7s forwards, burstPulse 4s ease-in-out 2.7s infinite;
        }
        .frost-burst-top {
          top: 5%;
          left: 50%;
          transform: translateX(-50%);
          width: 300px;
          height: 150px;
          animation: burstIn 1s ease-out 1.3s forwards, burstPulse 4s ease-in-out 2.3s infinite;
        }
        @keyframes burstIn {
          0% { opacity: 0; transform: scale(0.3); }
          60% { opacity: 0.8; transform: scale(1.3); }
          100% { opacity: 0.5; transform: scale(1); }
        }
        @keyframes burstPulse {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.15); }
        }

        /* Cold Air Vapor Trails */
        .vapor-trail {
          position: absolute;
          height: 2px;
          border-radius: 2px;
          opacity: 0;
          pointer-events: none;
          background: linear-gradient(90deg, transparent, rgba(103, 232, 249, 0.6), rgba(255, 255, 255, 0.8), rgba(103, 232, 249, 0.4), transparent);
        }
        .vapor-trail-1 {
          top: 30%;
          left: 0;
          width: 100%;
          animation: vaporDrift 6s linear 1.5s infinite;
        }
        .vapor-trail-2 {
          top: 42%;
          left: 0;
          width: 100%;
          height: 3px;
          animation: vaporDrift 5s linear 2s infinite;
        }
        .vapor-trail-3 {
          top: 55%;
          left: 0;
          width: 100%;
          animation: vaporDrift 7s linear 1.8s infinite;
        }
        .vapor-trail-4 {
          top: 65%;
          left: 0;
          width: 100%;
          height: 1px;
          animation: vaporDrift 5.5s linear 2.3s infinite;
        }
        .vapor-trail-5 {
          top: 38%;
          left: 0;
          width: 100%;
          height: 1px;
          animation: vaporDriftReverse 6.5s linear 1.6s infinite;
        }
        .vapor-trail-6 {
          top: 50%;
          left: 0;
          width: 100%;
          height: 2px;
          animation: vaporDriftReverse 5s linear 2.1s infinite;
        }
        @keyframes vaporDrift {
          0% { opacity: 0; transform: translateX(-100%) scaleX(0.5); }
          10% { opacity: 0.6; }
          50% { opacity: 0.4; }
          90% { opacity: 0.5; }
          100% { opacity: 0; transform: translateX(100%) scaleX(0.5); }
        }
        @keyframes vaporDriftReverse {
          0% { opacity: 0; transform: translateX(100%) scaleX(0.5); }
          10% { opacity: 0.5; }
          50% { opacity: 0.3; }
          90% { opacity: 0.4; }
          100% { opacity: 0; transform: translateX(-100%) scaleX(0.5); }
        }

        /* Frost Overlay on Image */
        .frost-overlay {
          position: absolute;
          inset: 0;
          border-radius: 1rem;
          background: 
            radial-gradient(ellipse 80% 40% at 50% 100%, rgba(103, 232, 249, 0.15) 0%, transparent 60%),
            radial-gradient(ellipse 60% 30% at 0% 50%, rgba(103, 232, 249, 0.1) 0%, transparent 60%),
            radial-gradient(ellipse 60% 30% at 100% 50%, rgba(103, 232, 249, 0.1) 0%, transparent 60%);
          pointer-events: none;
          animation: frostPulse 4s ease-in-out infinite;
        }
        @keyframes frostPulse {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }

        /* Text Glow */
        .text-glow {
          text-shadow: 0 0 20px rgba(255, 255, 255, 0.5),
                       0 0 40px rgba(103, 232, 249, 0.5),
                       0 0 60px rgba(103, 232, 249, 0.3);
        }

        /* Pulsing Text */
        .pulse-text {
          animation: textPulse 2s ease-in-out infinite;
        }

        @keyframes textPulse {
          0%, 100% {
            opacity: 1;
            text-shadow: 0 0 20px currentColor;
          }
          50% {
            opacity: 0.8;
            text-shadow: 0 0 40px currentColor, 0 0 60px currentColor;
          }
        }

        /* Tagline Animation */
        .tagline-container {
          animation: fadeInUp 1s ease-out 0.5s forwards;
          opacity: 0;
        }

        .tagline-sub {
          animation: fadeInUp 1s ease-out 1s forwards;
          opacity: 0;
        }

        @keyframes fadeInUp {
          0% {
            opacity: 0;
            transform: translateY(30px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Ice Crystals */
        .ice-crystal {
          position: absolute;
          width: 20px;
          height: 20px;
          background: linear-gradient(135deg, rgba(103, 232, 249, 0.9), rgba(255, 255, 255, 0.95));
          clip-path: polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%);
          animation: crystalFloat 5s ease-in-out infinite;
          box-shadow: 0 0 20px rgba(103, 232, 249, 0.8);
        }

        .ice-crystal-1 { top: 10%; left: 5%; animation-delay: 0s; }
        .ice-crystal-2 { top: 15%; right: 8%; animation-delay: 1s; width: 15px; height: 15px; }
        .ice-crystal-3 { bottom: 20%; left: 8%; animation-delay: 2s; width: 25px; height: 25px; }
        .ice-crystal-4 { bottom: 15%; right: 5%; animation-delay: 0.5s; }
        .ice-crystal-5 { top: 40%; left: 3%; animation-delay: 1.5s; width: 12px; height: 12px; }
        .ice-crystal-6 { top: 35%; right: 3%; animation-delay: 2.5s; width: 18px; height: 18px; }

        @keyframes crystalFloat {
          0%, 100% {
            transform: translateY(0) rotate(0deg);
            opacity: 0.7;
          }
          50% {
            transform: translateY(-30px) rotate(180deg);
            opacity: 1;
          }
        }

        /* Logo Phase Lightning */
        .logo-lightning-bg {
          position: absolute;
          inset: 0;
          overflow: hidden;
        }

        .logo-bolt {
          position: absolute;
          width: 4px;
          height: 300px;
          background: linear-gradient(to bottom, transparent, #67E8F9, #fff, #67E8F9, transparent);
          opacity: 0;
          filter: blur(1px);
          box-shadow: 0 0 15px #67E8F9, 0 0 30px #67E8F9;
        }

        .logo-bolt-1 {
          left: 20%;
          top: 0;
          animation: boltFlash 2s ease-in-out infinite;
        }
        .logo-bolt-2 {
          right: 20%;
          top: 0;
          animation: boltFlash 2s ease-in-out infinite 0.5s;
        }
        .logo-bolt-3 {
          left: 35%;
          bottom: 0;
          transform: rotate(180deg);
          animation: boltFlash 2s ease-in-out infinite 1s;
        }
        .logo-bolt-4 {
          right: 35%;
          bottom: 0;
          transform: rotate(180deg);
          animation: boltFlash 2s ease-in-out infinite 1.5s;
        }

        @keyframes boltFlash {
          0%, 100% { opacity: 0; height: 0; }
          10% { opacity: 1; height: 300px; }
          20% { opacity: 0.5; }
          30% { opacity: 1; }
          40% { opacity: 0; height: 0; }
        }

        /* Energy Rings */
        .energy-ring {
          position: absolute;
          border: 2px solid rgba(103, 232, 249, 0.5);
          border-radius: 50%;
          opacity: 0;
        }

        .energy-ring-1 {
          width: 400px;
          height: 400px;
          animation: ringExpand 3s ease-out infinite;
        }
        .energy-ring-2 {
          width: 400px;
          height: 400px;
          animation: ringExpand 3s ease-out infinite 1s;
        }
        .energy-ring-3 {
          width: 400px;
          height: 400px;
          animation: ringExpand 3s ease-out infinite 2s;
        }

        @keyframes ringExpand {
          0% {
            transform: scale(0.5);
            opacity: 1;
            border-color: rgba(103, 232, 249, 0.8);
          }
          100% {
            transform: scale(2.5);
            opacity: 0;
            border-color: rgba(124, 58, 237, 0);
          }
        }

        /* Logo Glow */
        .logo-glow-effect {
          position: absolute;
          width: 600px;
          height: 600px;
          background: radial-gradient(circle, rgba(103, 232, 249, 0.4) 0%, rgba(124, 58, 237, 0.3) 40%, transparent 70%);
          filter: blur(50px);
          animation: logoGlowPulse 2s ease-in-out infinite;
        }

        @keyframes logoGlowPulse {
          0%, 100% { transform: scale(1); opacity: 0.7; }
          50% { transform: scale(1.2); opacity: 1; }
        }

        /* Bouncing Logo Animation */
        .bouncing-logo-container {
          display: flex;
          align-items: center;
          justify-content: center;
          animation: bounceIn 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards,
                     logoFloat 3s ease-in-out 1s infinite;
        }

        @keyframes bounceIn {
          0% {
            opacity: 0;
            transform: scale(0) rotate(-180deg);
          }
          50% {
            opacity: 1;
            transform: scale(1.2) rotate(10deg);
          }
          70% {
            transform: scale(0.9) rotate(-5deg);
          }
          100% {
            transform: scale(1) rotate(0deg);
          }
        }

        @keyframes logoFloat {
          0%, 100% {
            transform: translateY(0) scale(1);
          }
          50% {
            transform: translateY(-25px) scale(1.05);
          }
        }
      `}</style>
    </div>
  );
};

export default LogoAnimation;
